<template>
  <figure
    class="image-widget grid"
    :class="`image-widget-mx--${widget.horizontal_margin}`"
  >
    <BaseImage
      :image="widget.image"
      :img-sizes="imgSizes"
      :sizes="sizes"
      :width="widget.image.width ?? horizontal_margin === 'large' ? 797 : 1280"
      :height="widget.image.height ?? horizontal_margin === 'large' ? 582 : 280"
      :lazyload="lazyImage"
    />
    <figcaption
      v-if="widget.caption"
      class="image-description t-small wysiwyg"
      v-html="widget.caption"
    />
  </figure>
</template>

<script lang="ts" setup>
import { PublicImageWidget } from '@/service/__generated-api'
import { Widget } from '@/types/utils'
import {
  sizeWithAspectRatio,
  getAspectRatio
} from '@/utils/images/aspect-ratio'

const {
  widget: { horizontal_margin, image }
} = defineProps<Widget<PublicImageWidget>>()

const sizes = computed(() =>
  horizontal_margin === 'no_margin'
    ? '100vw'
    : horizontal_margin === 'small'
    ? '94vw'
    : '(min-width: 753px) 62vw, (min-width: 1280px) 62vw, 94vw'
)

const imgSizes = computed(() =>
  horizontal_margin === 'large'
    ? [
        { w: 797, h: 582 },
        { w: 482, h: 352 },
        { w: 340, h: 248 }
      ]
    : [
        sizeWithAspectRatio({
          w: 1280,
          aspectRatio: getAspectRatio(image, 1280 / 280)
        }),
        sizeWithAspectRatio({
          w: 768,
          aspectRatio: getAspectRatio(image, 753 / 160)
        }),
        sizeWithAspectRatio({
          w: 360,
          aspectRatio: getAspectRatio(image, 360 / 280)
        })
      ]
)
</script>

<style lang="scss" scoped>
.image-widget {
  .image-wrapper,
  .image-description {
    @include media-column(
      (
        xs: all,
        md: all,
        lg: all
      )
    );
  }

  &-mx--no_margin .image-wrapper,
  .image-description {
    @include media-column(
      (
        xs: all,
        md: all,
        lg: all
      )
    );
  }

  &-mx--small .image-wrapper,
  .image-description {
    @include media-column(
      (
        xs: 4,
        md: 8,
        lg: 12
      )
    );
  }

  &-mx--large .image-wrapper,
  .image-description {
    @include media-column(
      (
        xs: 4,
        md: 6 1,
        lg: 8 2
      )
    );
  }
}

.image-wrapper {
  width: 100%;
}

.image {
  object-fit: cover;
}

.image-description {
  grid-row: 2;
  width: 100%;
  margin-top: rem(8px);
  text-align: center;
}
</style>
